import { createChecklistStore } from "@features/onboarding/create-checklist-store";
import { onboardingApi, onboardingKeys } from "@/shared/services";
import { reactQueryUtils } from "@lib/react-query";
import { PLATFORM_CHECKLIST_TASKS } from "./checklist";

const store = createChecklistStore(
  "sd:onboarding-platform-v2:",
  PLATFORM_CHECKLIST_TASKS,
  { persist: false },
);

export const usePlatformChecklistStore = store.useStore;
export const usePlatformChecklistStoreShallow = store.useStoreShallow;
export const isPlatformTaskDone = store.isDone;

const pendingKeys = new Set<string>();
const inFlightKeys = new Set<string>();

const withInFlight = (keys: string[]) =>
  Array.from(new Set([...keys, ...Array.from(inFlightKeys)]));

/**
 * Optimistic: the row ticks immediately, the server confirms, and a rejection
 * (unknown key, not client-allowed, network) removes only that key again.
 * Keys with a request in flight are merged into every server snapshot so a
 * refetch resolving mid-request cannot untick them; the state query is
 * invalidated on settle. Before the first hydration (store `userId` unset)
 * the key is queued and replayed by `hydratePlatformChecklist`.
 */
export const markPlatformTaskDone = (key: string) => {
  const { completed, userId } = store.useStore.getState();
  if (completed.includes(key) || inFlightKeys.has(key)) return;
  if (!userId) {
    pendingKeys.add(key);
    return;
  }
  inFlightKeys.add(key);
  store.markDone(key);
  onboardingApi
    .completeTask(key)
    .then((state) => {
      inFlightKeys.delete(key);
      store.useStore
        .getState()
        .setCompleted(withInFlight(Object.keys(state.tasks)));
    })
    .catch(() => {
      inFlightKeys.delete(key);
      const { completed, setCompleted } = store.useStore.getState();
      setCompleted(completed.filter((k) => k !== key));
    })
    .finally(() =>
      reactQueryUtils.invalidateQueries([...onboardingKeys.state]),
    );
};

export const hydratePlatformChecklist = (userId: string, keys: string[]) => {
  const s = store.useStore.getState();
  s.hydrate(userId);
  s.setCompleted(withInFlight(keys));
  const queued = Array.from(pendingKeys);
  pendingKeys.clear();
  for (const key of queued) markPlatformTaskDone(key);
};
